import { authorizedFetch } from './httpClient';

const API_URL = process.env.REACT_APP_API_URL;

export type LiabilityStatus = 'PENDING' | 'PAID' | 'EXPIRED' | 'CANCELLED';

export interface OrganizationInfo {
  id: string;
  name: string;
  createdAt: string;
}

export interface ChildInfo {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PayerInfo {
  id: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  child: ChildInfo | null;
}

export interface CollectionInfo {
  id: string;
  name: string;
  description: string;
  amount: number;
  createdAt: string;
  deadline: string;
  organization: OrganizationInfo;
}

export interface CollectionLiabilityEntry {
  id: string;
  status: LiabilityStatus;
  paidAt: string | null;
  collection: CollectionInfo;
  payer: PayerInfo;
}

export interface LiabilitiesSummary {
  pendingCount: number;
  pendingAmount: number;
}

export interface PaginatedResponse<T> {
  rows: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface CollectionLiabilityFilters {
  childIds?: string[];
  statuses?: LiabilityStatus[];
  page?: number;
  size?: number;
}

export const getLiabilitiesSummary = async (): Promise<LiabilitiesSummary> => {
  const response = await authorizedFetch(`${API_URL}/api/collectionLiabilities/summary`);
  if (!response.ok) throw new Error('Failed to fetch liabilities summary');
  return response.json();
};

export const getCollectionLiabilities = async (
  filters: CollectionLiabilityFilters,
): Promise<PaginatedResponse<CollectionLiabilityEntry>> => {
  const params = new URLSearchParams();
  filters.childIds?.forEach((id) => params.append('childIds', id));
  filters.statuses?.forEach((status) => params.append('statuses', status));
  if (filters.page !== undefined) params.append('pagination.page', String(filters.page));
  if (filters.size !== undefined) params.append('pagination.pageSize', String(filters.size));

  const response = await authorizedFetch(`${API_URL}/api/collectionLiabilities?${params.toString()}`);
  if (!response.ok) throw new Error('Failed to fetch collection liabilities');
  return response.json();
};

// GetDetailsResult is unwrapped server-side by ResultResponseBodyAdvice: success returns the
// entity body directly (200), NotFound returns 404 with no {isSuccess,...} envelope.
export const getCollectionLiabilityDetails = async (id: string): Promise<CollectionLiabilityEntry | null> => {
  const response = await authorizedFetch(`${API_URL}/api/collectionLiabilities/${id}`);
  if (!response.ok) return null;
  return response.json();
};
